import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { useBrand } from "@/context/BrandContext";
import { InstallButton } from "@/components/PWAInstall";
import { Gift, PhoneCall, Plus, CheckCircle, CreditCard, DeviceMobile, Wallet } from "@phosphor-icons/react";

const C = { bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00", text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)" };
const HEAD = { fontFamily: "'Outfit', sans-serif" };
const PACKAGES = [{ usd: 5 }, { usd: 10 }, { usd: 20, popular: true }, { usd: 50 }];

export default function Welcome() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [showPackages, setShowPackages] = useState(false);
  const { data: wallet } = useQuery({ queryKey: ["wallet"], queryFn: async () => (await api.get("/wallet")).data });
  const { data: cfg } = useQuery({ queryKey: ["public-settings"], queryFn: async () => (await api.get("/settings/public")).data });
  const bundles = (cfg?.credit_bundles?.length ? cfg.credit_bundles : PACKAGES);

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope',sans-serif" }} className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md" data-testid="welcome-page">
        <div className="rounded-[2rem] border p-8 text-center shadow-2xl" style={{ background: C.surface, borderColor: C.border }}>
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: C.green }}><Gift size={34} weight="fill" className="text-white" /></div>
          <h1 style={HEAD} className="text-3xl font-black tracking-tight">Welcome to {brand.brand_name}! 🎉</h1>
          <p className="mt-3 text-base" style={{ color: C.sub }}>Your {money(wallet?.balance_cents ?? 100)} FREE calling credit is ready.</p>

          {!showPackages ? (
            <div className="mt-7 space-y-3">
              <button data-testid="welcome-start-calling" onClick={() => navigate("/app/call")}
                className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>
                <PhoneCall size={20} weight="fill" /> Start Calling
              </button>
              <button data-testid="welcome-add-credit" onClick={() => setShowPackages(true)}
                className="flex w-full items-center justify-center gap-2 rounded-full border-2 px-6 py-4 font-semibold transition-colors hover:bg-white/5" style={{ borderColor: C.border, color: C.text }}>
                <Plus size={20} weight="bold" /> Add More Credit
              </button>
              <button data-testid="welcome-skip" onClick={() => navigate("/app")} className="w-full pt-2 text-sm font-semibold" style={{ color: C.sub }}>Skip to dashboard</button>
            </div>
          ) : (
            <div className="mt-7">
              <div className="grid grid-cols-2 gap-3">
                {bundles.map((b) => (
                  <button key={b.usd} data-testid={`welcome-package-${b.usd}`} onClick={() => navigate("/app/wallet")}
                    className="relative rounded-2xl border-2 bg-[#111a2e] p-4 text-left transition-all hover:-translate-y-0.5" style={{ borderColor: b.popular ? C.orange : C.border }}>
                    {b.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[9px] font-bold text-white" style={{ background: C.orange }}>MOST POPULAR</span>}
                    <p style={HEAD} className="text-2xl font-black">${b.usd}</p>
                    {b.bonus > 0 && <p className="mt-1 text-[11px] font-bold" style={{ color: C.green }}>+${b.bonus} free</p>}
                  </button>
                ))}
              </div>
              <p className="mt-4 flex items-center justify-center gap-3 text-xs" style={{ color: C.sub }}>
                <span className="flex items-center gap-1"><CreditCard size={14} /> Card</span>
                <span className="flex items-center gap-1"><Wallet size={14} /> PayPal</span>
                <span className="flex items-center gap-1"><DeviceMobile size={14} /> MoMo & more</span>
              </p>
              <button data-testid="welcome-continue" onClick={() => navigate("/app/wallet")}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 font-bold text-white shadow-xl" style={{ background: C.orange }}>
                Continue to Payment
              </button>
              <button data-testid="welcome-back" onClick={() => setShowPackages(false)} className="mt-3 w-full text-sm font-semibold" style={{ color: C.sub }}>Back</button>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-center gap-3 rounded-2xl border p-4" style={{ background: C.card, borderColor: C.border }}>
          <CheckCircle size={20} weight="fill" style={{ color: C.green }} />
          <span className="text-sm" style={{ color: C.sub }}>Install iYeneh for faster access</span>
          <InstallButton testid="welcome-install" icon={false} className="rounded-full px-4 py-2 text-xs font-bold text-white" style={{ background: C.green }}>Install</InstallButton>
        </div>
      </div>
    </div>
  );
}
